import FileUpload from '@mui/icons-material/FileUpload';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Theme,
	useMediaQuery,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileUploader } from '@volvo/vcdk-react/FileUploader';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { unitInfoQuery, unitKeys } from 'api/queries';
import { ValidationErrorResponse } from 'api/v1/data-contracts';
import {
	ALLOWED_FILE_EXTENSIONS_STRING,
	ALLOWED_UPLOAD_MIME_TYPES,
	MAX_FILE_LIMIT,
	MAX_FILE_SIZE,
	MAX_FILE_SIZE_MB,
} from 'library/components/controls/files/fileUploadValidation';
import { ControlledCheckbox } from 'library/components/controls/form/ControlledCheckbox';
import { ControlledCommentBox } from 'library/components/controls/form/ControlledCommentBox';
import { COMMENT_BOX_MAX_LENGTH } from 'library/constants/Validations';
import { FileWarningDialog } from 'library/hooks/UseUploadController/FilesWarningDialog';
import { useFileSelectionHandler } from 'library/hooks/UseUploadController/useFileSelectionHandler';
import { getValidationErrorMessage } from 'library/hooks/useFormError';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';

interface AdditionalFilesFormType {
	comment: string | undefined;
	isLimitedVisibility: boolean;
}

export const ServiceAgreementFileUploader = (): React.JSX.Element => {
	const { t } = useTranslation([
		'fileUpload',
		'commonWizard',
		'commonButton',
		'unitOverview',
		'common',
		'administration',
	]);
	const unitIds = useResolvedUnitIds();

	const form = useForm<AdditionalFilesFormType>({
		defaultValues: {
			comment: '',
			isLimitedVisibility: true,
		},
	});

	const [open, setOpen] = useState(false);

	const {
		filesToUpload,
		fileUploaderError,
		setFileUploaderError,
		invalidFiles,
		warningDialogOpen,
		handleFilesSelected,
		handleFileUploaderError,
		clearFiles,
		warningDialogClose,
	} = useFileSelectionHandler();

	const fullScreen = useMediaQuery((theme: Theme) =>
		theme.breakpoints.only('xs')
	);
	const { enqueueSnackbar } = useSnackbar();

	const queryClient = useQueryClient();
	const { api } = useProductCenterApi();

	const uploadMutation = useMutation({
		mutationFn: (payload: {
			file: File;
			comment: string | undefined;
			isPublic: boolean;
		}) =>
			api.uploadServiceAgreementFiles({
				unitId: unitIds.id,
				...payload,
			}),
	});

	const resetForm = useCallback(() => {
		clearFiles();
		form.reset({ comment: '', isLimitedVisibility: true });
	}, [clearFiles, form]);

	const close = useCallback(() => {
		setOpen(false);
		resetForm();
	}, [resetForm]);

	const uploadFilesSequentially = useCallback(async () => {
		if (!unitIds) return;
		if (filesToUpload.length === 0) return;

		setFileUploaderError(null);

		try {
			const { comment, isLimitedVisibility } = form.getValues();
			const isPublic = !isLimitedVisibility;

			for (const file of filesToUpload) {
				await uploadMutation.mutateAsync({
					file,
					comment,
					isPublic: isPublic,
				});
			}

			await queryClient.invalidateQueries({
				queryKey: unitKeys.serviceAgreementFiles(unitIds.id),
			});

			await queryClient.invalidateQueries({
				queryKey: unitInfoQuery(api, unitIds.id).queryKey,
			});

			enqueueSnackbar(t('administration:upload-pi-sucessful'), {
				variant: 'success',
			});

			close();
		} catch (error) {
			const validationError = (
				error as AxiosError<ValidationErrorResponse>
			).response?.data;
			setFileUploaderError(
				getValidationErrorMessage(
					validationError,
					t('common:error-unexpected')
				)
			);
		}
	}, [
		api,
		close,
		enqueueSnackbar,
		filesToUpload,
		form,
		queryClient,
		setFileUploaderError,
		t,
		uploadMutation,
		unitIds,
	]);

	const isSaveDisabled = useMemo(
		() => uploadMutation.isPending || filesToUpload.length === 0,
		[filesToUpload.length, uploadMutation.isPending]
	);

	return (
		<>
			<Button
				startIcon={<FileUpload />}
				color="secondary"
				disabled={!unitIds}
				onClick={() => {
					resetForm();
					setOpen(true);
				}}>
				{t('fileUpload:upload-files-title')}
			</Button>

			{open && (
				<Dialog
					maxWidth={false}
					open={open}
					onClose={close}
					fullScreen={fullScreen}>
					<DialogContent>
						<Box
							sx={{
								display: 'grid',
								gap: 2,
							}}>
							{fileUploaderError && (
								<Alert
									severity="error"
									onClose={() => setFileUploaderError(null)}>
									{fileUploaderError}
								</Alert>
							)}
							<FileUploader
								maxFileSize={MAX_FILE_SIZE}
								required
								multiple
								onFilesRemoved={handleFilesSelected}
								onUploadError={handleFileUploaderError}
								allowedTypes={ALLOWED_UPLOAD_MIME_TYPES}
								label={t('fileUpload:upload-files-title')}
								helperText={t('fileUpload:upload-files-text')}
								onFilesSelected={handleFilesSelected}>
								{t('fileUpload:drag-and-drop-first')}
								<div>
									{t('fileUpload:drag-and-drop-second')}
								</div>
							</FileUploader>

							<ControlledCommentBox
								control={form.control}
								name={'comment'}
								label={t(
									'commonWizard:additional-comment-label'
								)}
								validation={{
									maxLength: {
										value: COMMENT_BOX_MAX_LENGTH,
										message:
											'commonWizard:validation-comment-box-max-length',
									},
								}}
							/>

							<ControlledCheckbox
								control={form.control}
								name="isLimitedVisibility"
								label={t(
									'unitOverview:limited-visibility-and-explain'
								)}
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button variant="outlined" onClick={close}>
							{t('commonButton:cancel')}
						</Button>
						<Button
							color="secondary"
							disabled={isSaveDisabled}
							variant="contained"
							onClick={() => void uploadFilesSequentially()}>
							{t('commonButton:save')}
						</Button>
					</DialogActions>
				</Dialog>
			)}

			{warningDialogOpen && (
				<FileWarningDialog
					amount={MAX_FILE_LIMIT}
					// Keep extensions user-friendly in the warning dialog.
					extensions={`image/*,${ALLOWED_FILE_EXTENSIONS_STRING}`}
					filteredFiles={invalidFiles}
					onClose={warningDialogClose}
					open={warningDialogOpen}
					size={MAX_FILE_SIZE_MB}
					sx={{ zIndex: 1500 }}
				/>
			)}
		</>
	);
};
